import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  CalendarIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  ChartBarIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';

interface HeaderProps {
  ageGroup: '6-9' | '10-13' | '14+';
  studentName?: string;
}

const Header: React.FC<HeaderProps> = ({ ageGroup, studentName = 'Student' }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Staff (admins + teachers) see the Analytics dashboard link
  const isStaff = user?.role === 'admin' || user?.role === 'teacher';
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const getThemeColors = () => {
    switch (ageGroup) {
      case '6-9':
        return 'bg-gradient-to-r from-yellow-400 to-orange-400';
      case '10-13':
        return 'bg-gradient-to-r from-blue-500 to-cyan-500';
      case '14+':
        return 'bg-gradient-to-r from-purple-600 to-indigo-600';
      default:
        return 'bg-gradient-to-r from-blue-500 to-purple-600';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '🌅 Good Morning';
    if (hour < 17) return '☀️ Good Afternoon';
    return '🌙 Good Evening';
  };

  return (
    // paddingTop keeps the coloured header running behind the iPhone status
    // bar while the content below it clears the time and battery. It resolves
    // to 0 where there is no inset, so Android and the web are unaffected.
    <header
      ref={mobileMenuRef}
      className={`${getThemeColors()} shadow-xl relative backdrop-blur-sm z-50`}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 md:py-5">
          {/* Logo and Brand */}
          <div className="flex items-center space-x-2 md:space-x-3 flex-1 md:flex-initial">
            <div className="bg-white p-1.5 rounded-xl shadow-lg transform hover:scale-105 hover:rotate-2 transition-all duration-300 hover:shadow-2xl flex-shrink-0">
              <img
                src="/knoozy-logo.png"
                alt="Knoozy Logo"
                className="h-10 w-10 md:h-14 md:w-14 object-contain"
              />
            </div>
            <div className="text-white min-w-0">
              <h1 className={`font-bold tracking-tight ${ageGroup === '6-9' ? 'text-2xl md:text-3xl font-kid' : 'text-xl md:text-3xl'}`}>
                Knoozy
              </h1>
              <p className="text-xs md:text-sm opacity-90 font-medium">
                {ageGroup === '6-9' ? 'Let\'s Explore!' :
                 ageGroup === '10-13' ? 'Stay Informed!' :
                 'Your Daily Digest'}
              </p>
            </div>
          </div>

          {/* Mobile Greeting & Student Name - Shown on mobile */}
          <div className="md:hidden flex flex-col items-end text-white text-right mr-2">
            <p className="text-xs opacity-90 font-medium truncate max-w-[120px]">{getGreeting()}</p>
            <p className="font-bold text-sm truncate max-w-[120px]">{studentName}</p>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
            <div className="text-white text-right">
              <p className="text-xs opacity-90 font-medium">{getGreeting()}</p>
              <p className="font-bold text-sm">{studentName}</p>
            </div>

            <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-md px-4 py-2.5 rounded-xl shadow-lg hover:bg-white/30 transition-all duration-300 hover:shadow-xl">
              <CalendarIcon className="h-5 w-5 text-white" />
              <span className="text-white font-semibold text-sm">
                {format(new Date(), 'EEEE, MMM d')}
              </span>
            </div>

            {isStaff ? (
              <button
                onClick={() => navigate('/analytics')}
                className="bg-white/20 backdrop-blur-md text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-white/30 active:scale-95 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center space-x-2"
              >
                <ChartBarIcon className="h-5 w-5" />
                <span>Analytics</span>
              </button>
            ) : (
              <button
                onClick={() => navigate('/leaderboard')}
                className="bg-white/20 backdrop-blur-md text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-white/30 active:scale-95 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center space-x-2"
              >
                <TrophyIcon className="h-5 w-5" />
                <span>Leaderboard</span>
              </button>
            )}

            <button
              onClick={handleLogout}
              className="bg-white text-gray-800 px-5 py-2.5 rounded-xl font-semibold hover:bg-red-50 hover:text-red-600 active:scale-95 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center space-x-2"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2.5 rounded-lg hover:bg-white/20 active:scale-95 transition-all duration-200"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white shadow-2xl z-[60] border-t-2 border-gray-100 animate-slideDown">
          <div className="px-4 py-4 space-y-3">
            <div className="flex items-center space-x-3 bg-gray-50 p-3 rounded-xl">
              <CalendarIcon className="h-5 w-5 text-gray-600" />
              <span className="text-gray-700 font-medium text-sm">
                {format(new Date(), 'EEEE, MMMM d, yyyy')}
              </span>
            </div>

            {isStaff ? (
              <button
                onClick={() => { setIsMobileMenuOpen(false); navigate('/analytics'); }}
                className="w-full bg-indigo-600 text-white px-4 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-indigo-700 active:scale-98 transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
              >
                <ChartBarIcon className="h-5 w-5" />
                <span>Analytics Dashboard</span>
              </button>
            ) : (
              <button
                onClick={() => { setIsMobileMenuOpen(false); navigate('/leaderboard'); }}
                className="w-full bg-indigo-600 text-white px-4 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-indigo-700 active:scale-98 transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
              >
                <TrophyIcon className="h-5 w-5" />
                <span>Class Leaderboard</span>
              </button>
            )}

            <button
              onClick={handleLogout}
              className="w-full bg-red-500 text-white px-4 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-red-600 active:scale-98 transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center space-x-2"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
